import { NgClass, NgTemplateOutlet } from '@angular/common'
import { Component, computed, DestroyRef, effect, ElementRef, inject, input, OnDestroy, OnInit, output, signal, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ValidationErrors, Validators } from '@angular/forms'
import { BillingService, roundMoney } from '@app/modules/payments/services/billing.service'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { environment } from '@environments/environment'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { firstValueFrom } from 'rxjs'
import { filter } from 'rxjs/operators'
import { OrganizationService } from '../../../../services/organization.service'
import { formatCardBrand, formatCardExpiry, getCardBrandIconUrl } from '../card-brand-icon'
import { BillingPaymentMethod } from '../payment-cards-modal/payment-cards-modal.component'

declare let Stripe: any

@Component({
  selector: 'app-billing-wallet-top-up-modal',
  standalone: true,
  imports: [ModalComponent, TranslateModule, NgClass, NgTemplateOutlet, NgxSkeletonLoaderModule, BasicInputComponent],
  templateUrl: './wallet-top-up-modal.component.html',
  styleUrl: './wallet-top-up-modal.component.scss'
})
export class WalletTopUpModalComponent implements OnInit, OnDestroy {
  public readonly modalId = input<string>('')
  /** When true, render inline without the modal overlay (e.g. as a step in resend confirmation). */
  public readonly embedded = input<boolean>(false)
  /** When embedded, controls when the form initializes and tears down. */
  public readonly active = input<boolean>(false)
  public readonly headerIcon = input<string>('')
  public readonly paymentMethods = input<BillingPaymentMethod[]>([])
  public readonly getCardBrandIconUrl = input<(brand?: string) => string>(getCardBrandIconUrl)
  public readonly formatCardBrand = input<(brand?: string) => string>(formatCardBrand)
  public readonly formatCardExpiry = input<(expMonth?: string | number, expYear?: string | number) => string>(formatCardExpiry)

  /**
   * Optional custom amount to prefill when the modal opens (e.g. balance shortfall from resend flow).
   * When set, the custom amount field is selected instead of the default preset.
   */
  public readonly initialCustomAmount = input<number | null>(null)
  public readonly topUpSuccess = output<number>()
  /** Emitted when the user leaves embedded top-up via Back. */
  public readonly cancelRequested = output<void>()

  @ViewChild('cardElement') cardElementRef?: ElementRef<HTMLElement>

  public readonly presetAmounts = [100, 250, 500, 750, 1000, 1500]
  public readonly minCustomAmount = 50
  public readonly basicFieldType = BasicFieldType

  public selectedPresetAmount = signal<number>(100)
  /** Reactive mirror of the custom amount for computed state like preset selection. */
  public customAmount = signal('')
  public customAmountControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.pattern(/^\d+(\.\d{1,2})?$/), (control) => this._validateCustomAmount(control.value)]
  })
  public selectedPaymentMethodId = signal<string | null>(null)
  public isInitializingStripe = signal(false)
  public isConfirming = signal(false)
  public setAsDefault = signal(false)
  public cardError = signal('')
  /** True only after the user explicitly clicks "Add new card" while saved cards exist. */
  public userChoseNewCard = signal(false)

  public hasSavedCards = computed(() => this.paymentMethods().length > 0)

  /** Show Stripe input when there are no saved cards, user chose new card, or all saved cards are expired. */
  public showNewCardStripe = computed(() => {
    if (!this.hasSavedCards()) return true
    if (this.userChoseNewCard()) return true

    return !this.paymentMethods().some((method) => !this.isExpired(method))
  })

  public showSetAsDefaultCheckbox = computed(() => this.hasSavedCards() && this.userChoseNewCard())

  /**
   * Whether a new card entered during top-up should be saved to the customer profile.
   */
  public shouldSaveNewCard = computed(() => {
    if (!this.showNewCardStripe()) return false
    if (!this.hasSavedCards()) return true
    return this.setAsDefault()
  })

  public effectiveAmount = computed(() => {
    const customValue = this.customAmount().trim()

    if (customValue) {
      if (this.customAmountControl.invalid) {
        return 0
      }

      return roundMoney(Number(customValue))
    }

    return this.selectedPresetAmount()
  })

  public isCustomAmountActive = computed(() => this.customAmount().trim().length > 0)

  public isAmountValid = computed(() => {
    if (!this.isCustomAmountActive()) {
      return this.selectedPresetAmount() > 0
    }

    return this.customAmountControl.valid
  })

  /**
   * Whether the user can submit the top-up.
   * Disabled when amount is invalid or an async operation is in progress.
   */
  public canConfirm = computed(() => {
    if (!this.isAmountValid() || this.isConfirming() || this.isInitializingStripe()) {
      return false
    }

    if (this.showNewCardStripe()) {
      return true
    }

    return !!this.selectedPaymentMethodId()
  })

  private readonly _modalOpen = signal(false)

  private readonly _modalService = inject(ModalService)
  private readonly _billingService = inject(BillingService)
  private readonly _organizationService = inject(OrganizationService)
  private readonly _alertService = inject(AlertService)
  private readonly _translateService = inject(TranslateService)
  private readonly _destroyRef = inject(DestroyRef)

  /**
   * When saved cards load after the modal opened with none, switch to saved-card mode.
   */
  private readonly _syncLoadedPaymentMethods = effect(() => {
    if (!this._modalOpen() || this.userChoseNewCard()) return

    const methods = this.paymentMethods()
    if (methods.length === 0) return

    const hasUsableCard = methods.some((method) => !this.isExpired(method))
    if (!hasUsableCard) return

    if (!this.selectedPaymentMethodId() || this._card) {
      this._selectInitialSavedCard()
    }
  })

  private _stripe: any = null
  private _card: any = null
  /** Incremented to cancel in-flight Stripe initialization when saved cards load. */
  private _stripeInitId = 0

  private readonly _cardStyle = {
    base: {
      color: '#374151',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '14px',
      '::placeholder': {
        color: '#9CA3AF'
      }
    },
    invalid: {
      color: '#DC2626'
    }
  }

  private readonly _syncEmbeddedLifecycle = effect(() => {
    if (!this.embedded()) return

    if (this.active()) {
      this._initializeModal()
    } else {
      this._teardownForm()
    }
  })

  /**
   * Subscribe to modal open/close and initialize or reset the top-up form.
   */
  ngOnInit(): void {
    if (this.embedded()) return

    this._modalService.modalState$
      .pipe(
        filter((state) => !!this.modalId() && this.modalId() in state),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((state) => {
        const id = this.modalId()

        if (state[id]) {
          this._modalOpen.set(true)
          this._initializeModal()
        } else {
          this._modalOpen.set(false)
          this._teardownForm()
        }
      })
  }

  /**
   * Tear down Stripe and form state when the component is destroyed (embedded step navigation).
   */
  ngOnDestroy(): void {
    this._teardownForm()
  }

  /**
   * Close the wallet top-up modal.
   */
  public close(): void {
    const id = this.modalId()
    if (id && this._modalService.isModalOpen(id)) {
      this._modalService.toggleModal(id)
    }
  }

  /**
   * Leave embedded top-up and return to the parent flow (e.g. resend cost step).
   */
  public cancelEmbedded(): void {
    if (this.isConfirming()) return
    this.cancelRequested.emit()
  }

  /**
   * Select a preset top-up amount.
   * @param {number} amount Preset amount in USD
   */
  public selectPresetAmount(amount: number): void {
    this.selectedPresetAmount.set(amount)
    this.customAmount.set('')
    this.customAmountControl.setValue('', { emitEvent: false })
    this.customAmountControl.markAsPristine()
    this.customAmountControl.markAsUntouched()
    this.customAmountControl.updateValueAndValidity({ emitEvent: false })
    this.cardError.set('')
  }

  /**
   * Sanitize custom amount input while typing: digits, one decimal point, up to 2 fraction digits.
   * @param {string} value Raw input value
   */
  public onCustomAmountInput(value: string): void {
    const sanitizedValue = this._sanitizeCustomAmountInput(value)
    this.customAmount.set(sanitizedValue)
    this.customAmountControl.setValue(sanitizedValue, { emitEvent: false })
    this.customAmountControl.markAsDirty()
    this.customAmountControl.markAsTouched()
    this.customAmountControl.updateValueAndValidity({ emitEvent: false })
    this.cardError.set('')
  }

  /**
   * Resolve custom amount validation errors for dollar amounts with up to 2 decimal places.
   * @param {string} value Raw custom amount input
   * @returns {'invalid' | 'min' | null} Validation error type when invalid
   */
  private _getCustomAmountValidationError(value: string): 'invalid' | 'min' | null {
    const trimmed = value.trim()
    if (!trimmed) return null

    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
      return 'invalid'
    }

    const amount = Number(trimmed)
    if (!Number.isFinite(amount) || amount <= 0) {
      return 'invalid'
    }

    if (amount < this.minCustomAmount) {
      return 'min'
    }

    return null
  }

  /**
   * Map custom amount validation into FormControl errors used by the shared basic input.
   * @param {string} value Raw custom amount input
   * @returns {ValidationErrors | null} Angular validation errors for the custom amount field
   */
  private _validateCustomAmount(value: string): ValidationErrors | null {
    const error = this._getCustomAmountValidationError(value)

    switch (error) {
      case 'invalid':
        return { invalidNumber: true }
      case 'min':
        return { customAmountMinError: this._translateService.instant('v2.orgSettings.billing.customAmountMinError') }
      default:
        return null
    }
  }

  /**
   * Normalize custom amount input while typing: digits, one decimal separator, max 2 fraction digits.
   * @param {string} value Raw input value
   * @returns {string} Sanitized amount string safe to bind back to the input
   */
  private _sanitizeCustomAmountInput(value: string): string {
    let sanitized = value.replace(/[^\d.]/g, '')

    const firstDotIndex = sanitized.indexOf('.')
    if (firstDotIndex !== -1) {
      const intPart = sanitized.slice(0, firstDotIndex)
      const decPart = sanitized
        .slice(firstDotIndex + 1)
        .replace(/\./g, '')
        .slice(0, 2)
      sanitized = `${intPart}.${decPart}`
    }

    return sanitized
  }

  /**
   * Select a saved payment method for the top-up.
   * @param {BillingPaymentMethod} method Saved payment method
   */
  public selectSavedCard(method: BillingPaymentMethod): void {
    if (this.isExpired(method)) return

    this.userChoseNewCard.set(false)
    this.setAsDefault.set(false)
    this.cardError.set('')
    this.selectedPaymentMethodId.set(method.id)
    this._teardownStripeElement()
  }

  /**
   * Switch to the inline new-card flow for a one-time or saved top-up.
   */
  public selectNewCard(): void {
    this.userChoseNewCard.set(true)
    this.selectedPaymentMethodId.set(null)
    this.setAsDefault.set(false)
    this.cardError.set('')

    this._initializeStripeCard()
  }

  /**
   * Toggle whether a newly entered card should be saved as the default payment method.
   * @param {boolean} checked Checkbox state
   */
  public toggleSetAsDefault(checked: boolean): void {
    this.setAsDefault.set(checked)
  }

  /**
   * Whether the given payment method is the organization default.
   * @param {BillingPaymentMethod} method Saved payment method
   * @returns {boolean} True when the card is default
   */
  public isDefault(method: BillingPaymentMethod): boolean {
    return !!method?.is_default
  }

  /**
   * Whether the given payment method is expired.
   * @param {BillingPaymentMethod} method Saved payment method
   * @returns {boolean} True when the card is expired
   */
  public isExpired(method: BillingPaymentMethod): boolean {
    const month = Number(method?.exp_month)
    const year = Number(method?.exp_year)
    if (!month || !year) return false

    const now = new Date()
    const expiry = new Date(year, month, 0, 23, 59, 59, 999)
    return expiry.getTime() < now.getTime()
  }

  /**
   * Whether the given saved card row is selected.
   * @param {BillingPaymentMethod} method Saved payment method
   * @returns {boolean} True when the card is selected
   */
  public isSelectedCard(method: BillingPaymentMethod): boolean {
    return !this.userChoseNewCard() && this.selectedPaymentMethodId() === method.id
  }

  /**
   * Gets skeleton theme colors based on current theme.
   */
  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }

  /**
   * Confirm the wallet top-up using a saved card or a new card.
   */
  public confirm(): void {
    if (!this.canConfirm()) return

    this.isConfirming.set(true)
    this.cardError.set('')

    if (this.showNewCardStripe()) {
      this._confirmWithNewCard()
      return
    }

    this._confirmWithSavedCard()
  }

  /**
   * Reset the modal when it opens and preselect a default saved card when available.
   */
  private _initializeModal(): void {
    this._teardownForm(true)
    this.userChoseNewCard.set(false)

    const prefillAmount = this.initialCustomAmount()
    if (prefillAmount != null && prefillAmount > 0) {
      const prefilledValue = this._formatCustomAmountForInput(prefillAmount)
      this.customAmount.set(prefilledValue)
      this.customAmountControl.setValue(prefilledValue, { emitEvent: false })
      this.customAmountControl.markAsTouched()
      this.customAmountControl.markAsDirty()
      this.customAmountControl.updateValueAndValidity({ emitEvent: false })
    } else {
      this.selectedPresetAmount.set(100)
      this.customAmount.set('')
      this.customAmountControl.setValue('', { emitEvent: false })
      this.customAmountControl.markAsPristine()
      this.customAmountControl.markAsUntouched()
      this.customAmountControl.updateValueAndValidity({ emitEvent: false })
    }

    if (this.paymentMethods().length > 0) {
      this._selectInitialSavedCard()
      return
    }

    this.setAsDefault.set(true)
    this._initializeStripeCard()
  }

  /**
   * Format a dollar amount for the custom amount input (no trailing zeros).
   * @param {number} amount Amount in USD
   * @returns {string} Display value for the custom amount field
   */
  private _formatCustomAmountForInput(amount: number): string {
    const rounded = roundMoney(amount)
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2)
  }

  /**
   * Select the default saved card, or the first usable card, when opening the modal.
   */
  private _selectInitialSavedCard(): void {
    const methods = this.paymentMethods()
    const defaultCard = methods.find((method) => method.is_default && !this.isExpired(method))
    const firstUsableCard = methods.find((method) => !this.isExpired(method))
    const initialCard = defaultCard ?? firstUsableCard ?? null

    this.userChoseNewCard.set(false)
    this.setAsDefault.set(false)
    this.selectedPaymentMethodId.set(initialCard?.id ?? null)
    this._teardownStripeElement()

    if (!initialCard) {
      this.userChoseNewCard.set(true)
      this.setAsDefault.set(true)
      this._initializeStripeCard()
    }
  }

  /**
   * Top up the wallet with a saved payment method.
   */
  private _confirmWithSavedCard(): void {
    const paymentMethodId = this.selectedPaymentMethodId()
    if (!paymentMethodId) {
      this.isConfirming.set(false)
      return
    }

    this._organizationService
      .topUpOrganizationWallet(this._billingService.workspaceId, {
        amount: this.effectiveAmount(),
        payment_method_id: paymentMethodId
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._finishTopUp(),
        error: () => {
          this.cardError.set(this._translateService.instant('v2.orgSettings.billing.topUpError'))
          this.isConfirming.set(false)
        }
      })
  }

  /**
   * Top up the wallet with a newly entered card via `/billing/top-up/payment-intent` and Stripe.js confirmation.
   */
  private async _confirmWithNewCard(): Promise<void> {
    if (!this._stripe || !this._card) {
      this.isConfirming.set(false)
      return
    }

    const confirmOptions: Record<string, unknown> = {
      payment_method: {
        card: this._card
      }
    }

    if (this.shouldSaveNewCard()) {
      confirmOptions['setup_future_usage'] = 'off_session'
    }

    try {
      const intentResult = await firstValueFrom(
        this._organizationService.createOrganizationWalletTopUpPaymentIntent(this._billingService.workspaceId, {
          amount: this.effectiveAmount()
        })
      )
      const clientSecret = intentResult?.client_secret ?? null

      if (!clientSecret) {
        throw new Error('Missing payment intent client secret')
      }

      const result: any = await this._stripe.confirmCardPayment(clientSecret, confirmOptions)

      if (result.error) {
        this.cardError.set(result.error.message ?? this._translateService.instant('v2.orgSettings.billing.topUpError'))
        this.isConfirming.set(false)
        return
      }

      if (result.paymentIntent?.status !== 'succeeded') {
        this.cardError.set(this._translateService.instant('v2.orgSettings.billing.topUpError'))
        this.isConfirming.set(false)
        return
      }

      if (this.shouldSaveNewCard()) {
        const paymentMethodId = this._extractPaymentMethodId(result.paymentIntent?.payment_method)
        if (paymentMethodId) {
          try {
            await firstValueFrom(this._organizationService.setDefaultPaymentMethod(this._billingService.workspaceId, paymentMethodId))
          } catch {
            // Top-up succeeded even if default update fails.
          }
        }
      }

      this._finishTopUp()
    } catch {
      this.cardError.set(this._translateService.instant('v2.orgSettings.billing.topUpError'))
      this.isConfirming.set(false)
    }
  }

  /**
   * Extract a Stripe payment method ID from a PaymentIntent payment_method field.
   * @param {unknown} paymentMethod Stripe payment method reference
   * @returns {string | null} Payment method ID when available
   */
  private _extractPaymentMethodId(paymentMethod: unknown): string | null {
    if (typeof paymentMethod === 'string') return paymentMethod
    if (paymentMethod && typeof paymentMethod === 'object' && 'id' in paymentMethod) {
      return String((paymentMethod as { id: string }).id)
    }

    return null
  }

  /**
   * Reload billing data, show success feedback, and close the modal.
   */
  private _finishTopUp(): void {
    const amount = this.effectiveAmount()
    this.topUpSuccess.emit(amount)
    this._billingService.reloadBillingDetails$.next('top-up-success')
    this._alertService.alert(Alert.SUCCESS, this._translateService.instant('v2.orgSettings.billing.topUpSuccess'))
    this.isConfirming.set(false)

    if (!this.embedded()) {
      this.close()
    }
  }

  /**
   * Load Stripe.js and mount the card element without creating a payment intent.
   * Aborts if saved cards load before initialization finishes.
   */
  private async _initializeStripeCard(): Promise<void> {
    this._teardownStripeElement()

    const initId = ++this._stripeInitId
    this.isInitializingStripe.set(true)
    this.cardError.set('')

    try {
      await this._loadStripeScript()

      if (initId !== this._stripeInitId || !this.showNewCardStripe()) {
        this.isInitializingStripe.set(false)
        return
      }

      this._stripe = Stripe(environment.stripeKey)
      this.isInitializingStripe.set(false)

      setTimeout(() => {
        if (initId !== this._stripeInitId || !this.showNewCardStripe()) return
        this._mountCardElement()
      }, 0)
    } catch {
      if (initId !== this._stripeInitId) return

      this.cardError.set(this._translateService.instant('v2.orgSettings.billing.topUpError'))
      this.isInitializingStripe.set(false)
    }
  }

  /**
   * Mount the Stripe card element into the modal container.
   */
  private _mountCardElement(): void {
    if (!this.showNewCardStripe()) return

    const container = this.cardElementRef?.nativeElement
    if (!container || !this._stripe) return

    const elements = this._stripe.elements()
    this._card = elements.create('card', { style: this._cardStyle })
    this._card.mount(container)
    this._card.addEventListener('change', (event: any) => {
      this.cardError.set(event.error?.message ?? '')
    })
  }

  /**
   * Unmount Stripe elements and cancel any in-flight Stripe initialization.
   */
  private _teardownStripeElement(): void {
    this._stripeInitId++

    if (this._card) {
      this._card.unmount()
      this._card = null
    }

    this.isInitializingStripe.set(false)
  }

  /**
   * Reset modal state when it closes, optionally keeping Stripe teardown only.
   * @param {boolean} [resetAll=true] Whether to reset all form fields
   */
  private _teardownForm(resetAll = true): void {
    this._teardownStripeElement()

    if (!resetAll) return

    this._stripe = null
    this.selectedPresetAmount.set(100)
    this.customAmount.set('')
    this.customAmountControl.setValue('', { emitEvent: false })
    this.customAmountControl.markAsPristine()
    this.customAmountControl.markAsUntouched()
    this.customAmountControl.updateValueAndValidity({ emitEvent: false })
    this.selectedPaymentMethodId.set(null)
    this.userChoseNewCard.set(false)
    this.isConfirming.set(false)
    this.setAsDefault.set(false)
    this.cardError.set('')
  }

  /**
   * Ensure Stripe.js is available before initializing Elements.
   */
  private _loadStripeScript(): Promise<void> {
    if (typeof Stripe !== 'undefined') {
      return Promise.resolve()
    }

    const existingScript = document.querySelector('script[data-payment="stripe"]')
    if (existingScript) {
      return new Promise((resolve) => {
        existingScript.addEventListener('load', () => resolve(), { once: true })
        if (typeof Stripe !== 'undefined') resolve()
      })
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.setAttribute('src', 'https://js.stripe.com/v3/')
      script.dataset['payment'] = 'stripe'
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Stripe.js'))
      document.head.appendChild(script)
    })
  }
}
